import { FormattedMessage } from 'react-intl'
import { Box, FormHelperText, TextField, TextFieldProps, styled, FormControl, InputLabel, Tooltip } from '@mui/material'
import { ArrowUpwardOutlined, StopCircleOutlined } from '@mui/icons-material'
import { Button, IconButton, LabelWithIndicator } from '@carto/meridian-ds/components'
import { useIntlMessages } from 'app/localization/useIntlMessages'

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5)
}))

const Content = styled(Box, {
  shouldForwardProp: (prop: string) => !['hasToolbar'].includes(prop)
})<{
  hasToolbar?: boolean
}>(({ hasToolbar }) => ({
  display: 'flex',
  alignItems: 'flex-end',

  ...(hasToolbar && {
    display: 'block'
  })
}))

const TextFieldBase = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    minHeight: 'auto !important',

    '&:before': {
      border: `none !important`,

      '&:hover': {
        border: `none !important`
      }
    },
    '& textarea': {
      maxHeight: theme.spacing(32), // 256px, defined in the design specs
      overflowY: 'auto !important',
      boxSizing: 'border-box'
    },

    // Sizes
    '&.MuiInputBase-multiline.MuiInputBase-sizeSmall textarea': {
      padding: theme.spacing(0.75, 1.25)
    }
  },
  // Variant's overrides
  '& .MuiFilledInput-root': {
    backgroundColor: 'transparent !important'
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent !important',

    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none !important'
    }
  }
}))

const InputWrapper = styled(Box, {
  shouldForwardProp: (prop: string) => !['variant', 'disabled', 'error'].includes(prop)
})<{
  variant?: TextFieldProps['variant']
  disabled?: boolean
  error?: boolean
}>(({ theme, variant, disabled, error }) => ({
  borderRadius: theme.spacing(0.5),
  transition: theme.transitions.create(['background-color', 'box-shadow'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shortest
  }),

  // States
  ...(error && {
    boxShadow: `0 0 0 2px inset ${theme.palette.error.light}`
  }),

  // Variants
  ...(variant === 'filled' && {
    backgroundColor: theme.palette.default.background,

    '&:hover': {
      backgroundColor: !disabled && theme.palette.action.hover
    },
    '&:focus-within': {
      backgroundColor: theme.palette.default.background
    }
  }),
  ...(variant === 'outlined' && {
    boxShadow: `0 0 0 1px inset ${theme.palette.default.outlinedBorder}`

    // TBD when we need to support outlined variant
  })
}))

const ButtonWrapper = styled(Box, {
  shouldForwardProp: (prop: string) => !['size'].includes(prop)
})<{
  size?: TextFieldProps['size']
}>(({ theme, size }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(0.5),
  padding: theme.spacing(size === 'small' ? 0.5 : 1)
}))

const Toolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),

  // Reset margin
  '& .MuiButtonBase-root, & .optionIconButton': {
    marginLeft: 0
  }
}))

export type MessageFieldProps = Omit<TextFieldProps, 'InputProps' | 'onClick'> & {
  value?: string
  defaultValue?: string
  onClick: () => void
  /**
   * Called when stop icon is clicked
   */
  onStop?: () => void
  analyzing?: boolean
  iconButton?: React.ReactNode
  labelButton?: string
  disabledButton?: boolean
  validationType?: 'optional' | 'required'
  textFieldProps?: TextFieldProps
  toolbar?: React.ReactNode
  header?: React.ReactNode
}

const messageIds = {
  stop: 'common.button.stop',
  typeToSend: 'common.button.typeToSend',
  send: 'common.button.send'
}
/**
 * MessageField - customized TextField with following specific features:
 *  * hover/focused states according to design
 *  * analize/stop icons according to design
 */
export default function MessageField({
  value,
  defaultValue,
  label,
  onChange,
  onClick,
  onStop,
  disabled,
  variant = 'filled',
  size = 'small',
  analyzing,
  iconButton = <ArrowUpwardOutlined />,
  labelButton,
  disabledButton,
  helperText,
  error,
  fullWidth = true,
  validationType,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  focused: _focused,
  onKeyDown,
  textFieldProps,
  toolbar,
  header,
  ...otherProps
}: MessageFieldProps) {
  const messages = useIntlMessages(messageIds)
  const inputIsDisabled = disabled || analyzing
  // Use defaultValue for determining if button should be disabled when in uncontrolled mode
  const currentValue = value ?? defaultValue ?? ''
  const buttonIsDisabled = currentValue === '' || disabledButton || disabled

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      if (e.ctrlKey || e.shiftKey) {
        // Add a line break when the user presses "Enter" and "Control/Shift" keys simultaneously
        e.preventDefault()
        const textarea = e.target as HTMLTextAreaElement
        const cursorPosition = (e.target as HTMLTextAreaElement).selectionStart
        const textValue = textarea.value

        const newValue = textValue.slice(0, cursorPosition) + '\n' + textValue.slice(cursorPosition)

        const onChangeHandler = textFieldProps?.onChange || onChange
        if (onChangeHandler) {
          const newEvent = {
            ...e,
            target: {
              ...e.target,
              value: newValue
            }
          } as unknown as React.ChangeEvent<HTMLTextAreaElement>
          onChangeHandler(newEvent)

          // Set the cursor position after the line break
          requestAnimationFrame(() => {
            textarea.setSelectionRange(cursorPosition + 1, cursorPosition + 1)
          })
        }
      } else {
        // Prevent default behavior of Enter key press only
        e.preventDefault()
        onClick()
      }
    }
    const onKeyDownHandler = textFieldProps?.onKeyDown || onKeyDown
    onKeyDownHandler?.(e)
  }
  return (
    <FormControl fullWidth={fullWidth} size={size} error={error} disabled={inputIsDisabled}>
      {label && (
        <InputLabel shrink={true}>
          <LabelWithIndicator label={label} type={validationType} />
        </InputLabel>
      )}

      <InputWrapper variant={variant} disabled={inputIsDisabled} error={error}>
        {header && <Header>{header}</Header>}

        <Content hasToolbar={!!toolbar}>
          <TextFieldBase
            multiline
            {...(value !== undefined ? { value } : { defaultValue })}
            onChange={onChange}
            focused={false}
            disabled={inputIsDisabled}
            variant={variant}
            size={size}
            fullWidth={fullWidth}
            required={validationType === 'required'}
            {...otherProps}
            {...textFieldProps}
            onKeyDown={handleKeyDown}
          />

          <ButtonWrapper size={size}>
            {toolbar && <Toolbar>{toolbar}</Toolbar>}
            <>
              {analyzing ? (
                <IconButton
                  onClick={onStop}
                  color="default"
                  size={size}
                  icon={<StopCircleOutlined />}
                  tooltip={messages.stop}
                  aria-label={messages.stop}
                  tooltipPlacement="top"
                />
              ) : (
                <>
                  {iconButton && !labelButton && (
                    <IconButton
                      onClick={onClick}
                      disabled={buttonIsDisabled}
                      color="primary"
                      variant="contained"
                      size={size}
                      icon={iconButton}
                      tooltip={!disabled ? (buttonIsDisabled ? messages.typeToSend : messages.send) : undefined}
                      aria-label={buttonIsDisabled ? messages.typeToSend : messages.send}
                      tooltipPlacement="top"
                    />
                  )}
                  {labelButton && (
                    <Tooltip title={buttonIsDisabled && !disabled ? messages.typeToSend : undefined}>
                      <Box display="flex">
                        <Button
                          onClick={onClick}
                          variant="contained"
                          color="primary"
                          disabled={buttonIsDisabled}
                          size={size}
                        >
                          <FormattedMessage id={labelButton} />
                        </Button>
                      </Box>
                    </Tooltip>
                  )}
                </>
              )}
            </>
          </ButtonWrapper>
        </Content>
      </InputWrapper>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}
